import React, { useState, useEffect } from "react";
import "../styles/LayoutEditor.css";
import Modal from "./Modal";
import FolderTreeManager from "./FolderTreeManager";
import { Boolean, Calculation, Checkbox, Contact, ContactList, DateInput, Deadline, DocGen, Dropdown, FileUpload, Instructions, MultiFile, MultiSelect, NumberInput, SearchSelect, Subheader, Text, TimeInput } from "./FieldComponents";
import { IconMap } from "./IconMap";
import { StarOutlineSharp, Close, SettingsVoice } from "@mui/icons-material";
import { Edit, Loader2, Trash2, Archive, ArchiveX, ChevronDown } from "lucide-react";
import { useOptimizedResize } from '../utils/performance';
import { useNavVariant } from "../App";
import MatterPublicFormField from './MatterPublicFormField';
import FieldRecommendationSettings from './FieldRecommendationSettings';

const Vital = ({ setEditVital, setVitalData, deleteVital, vital, description, edit }) => {
    return (
        <div className="vital-item">
            <div className='vital-content'>
                <h4>{vital}</h4>
                <p>{description}</p>
            </div>
            {edit && <div className='vital-actions'>
                <Edit onClick={() => {
                    setEditVital(vital.id);
                    setVitalData({ 
                        vital_name: vital.name, 
                        vital_description: vital.description,
                        vital_field: vital.field_id 
                    });
                }} />
                <Trash2 onClick={() => {
                    if (window.confirm(`Are you sure you want to delete the "${vital.name}" vital?`)) {
                        deleteVital(vital.id);
                    }
                }} />
            </div>}
        </div>
    );
}

export const LayoutEditor = () => {
    const [sections, setSections] = useState([]);
    const [phases, setPhases] = useState([]);
    const [templates, setTemplates] = useState([]);
    const navVariant = useNavVariant();
    const [createTemplate, setCreateTemplate] = useState(false);
    const [createNew, setCreateNew] = useState(null);
    const [defaultTemplate, setDefaultTemplate] = useState(null);
    const [isSaving, setIsSaving] = useState(null);
    const [formData, setFormData] = useState({
        name: "",
        aka: "",
        description: "",
        visibility: 0,
        default: 0,
        favorite: 0,
    });
    const [sectionData, setSectionData] = useState({ name: "", description: "", icon: "Star", order_id: 0 });
    const [addingField, setAddingField] = useState(null);
    const [selectedField, setSelectedField] = useState("");
    const [sectionExpanded, setSectionExpanded] = useState(false);
    const [editingSection, setEditingSection] = useState(null);
    const [customFieldData, setCustomFieldData] = useState({ name: "", section_id: "", field_id: "", order_id: 0, rules: {} });
    const [currentFields, setCurrentFields] = useState([]);
    const [showIconModal, setShowIconModal] = useState(false);
    const [selectedTemplateHeader, setSelectedTemplateHeader] = useState(0);
    const [phaseData, setPhaseData] = useState({ phase: "", description: "", template_id: 0, order_id: 0 });
    const [editPhase, setEditPhase] = useState(null);
    const [dragging, setDragging] = useState(null);
    const [vitalData, setVitalData] = useState({ name: '', description: ''});
    const [folderData, setFolderData] = useState({ name: "", parent_folder_id: 0, folder_access: "Standard" });
    const [folders, setFolders] = useState([]);
    const [fieldTypes, setFieldTypes] = useState([]);
    const [marketingSource, setMarketingSource] = useState({
        source_name: "",
        marketing_type: 0,
        description: "",
        template_id: 0,
        order_id: null
    });
    const [marketingSources, setMarketingSources] = useState([]);
    const [fieldLimit, setFieldLimit] = useState(4);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [hoveredField, setHoveredField] = useState(null);
    const [hoverTimeout, setHoverTimeout] = useState(null);
    const [statuses, setStatuses] = useState([]);
    const [statusData, setStatusData] = useState({ name: "", description: "" });
    const [statusExpanded, setStatusExpanded] = useState(null);
    const [caseTypes, setCaseTypes] = useState([]);
    const [currentSection, setCurrentSection] = useState(null);
    const [allFields, setAllFields] = useState([]);
    const [vitals, setVitals] = useState([]);
    const [editVital, setEditVital] = useState(null);
    const [editMarketingSource, setEditMarketingSource] = useState(null);
    const [editStatus, setEditStatus] = useState(null);
    const [includeArchivedSources, setIncludeArchivedSources] = useState(false);
    const [showSubsections, setShowSubsections] = useState([]);
    
    // Message Templates & Automation state
    const [messageTemplates, setMessageTemplates] = useState([]);
    const [automationRules, setAutomationRules] = useState([]);
    const [messageTemplateData, setMessageTemplateData] = useState({
        template_name: '',
        description: '',
        message_type: 'text',
        message_content: '',
        subject: '',
        email_from: '',
        attachment_s3_keys: []
    });
    const [automationRuleData, setAutomationRuleData] = useState({
        rule_name: '',
        description: '',
        trigger_type: 'case_created',
        trigger_conditions: {},
        action_type: 'message',
        message_template_id: '',
        notification_target: 'contact',
        target_config: {},
        send_to_field: 'email',
        delay_minutes: 0
    });
    const [editingMessageTemplate, setEditingMessageTemplate] = useState(null);
    const [editingAutomationRule, setEditingAutomationRule] = useState(null);
    const [showMessageTemplateModal, setShowMessageTemplateModal] = useState(false);
    const [showAutomationRuleModal, setShowAutomationRuleModal] = useState(false);
    
    // Helper function to sort sections with subsections below their parent sections
    const sortSectionsWithSubsections = (sections) => {
        const sortedSections = [];
        
        // First, get all parent sections (non-subsections) sorted by order_id
        const parentSections = sections
            .filter(section => !section.subsection || section.subsection === null || section.subsection === 0)
            .sort((a, b) => (a.order_id || 0) - (b.order_id || 0));
        
        // For each parent section, add it and its subsections
        parentSections.forEach(parentSection => {
            sortedSections.push(parentSection);
            
            // Find and add subsections for this parent, sorted by their order_id
            const subsections = sections
                .filter(section => section.subsection === parentSection.id)
                .sort((a, b) => (a.order_id || 0) - (b.order_id || 0));
            
            subsections.forEach(subsection => {
                sortedSections.push(subsection);
            });
        });
        
        return sortedSections;
    };
    
    const handleMouseEnter = (fieldId) => {
        if (hoverTimeout) clearTimeout(hoverTimeout);

        setHoveredField(fieldId);
    };
    
    const handleMouseLeave = () => {
        setHoveredField(null);
    };

    useOptimizedResize(() => {
        setScreenWidth(window.innerWidth);
    });

    const fetchFolderStructure = async (templateId) => {
        try {
            const response = await fetch(`https://api.casedb.co/folder-templates.php?template_id=${templateId}&time=${new Date().getTime()}`);
            const data = await response.json();
            setFolders(data.folders || []);
        } catch (error) {
            console.error("Error fetching folder structure:", error);
        }
    };

    const fetchStatuses = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/statuses.php?time=${new Date().getTime()}`);
            const data = await response.json();
            setStatuses(data.statuses || []);
        } catch (error) {
            console.error("Error fetching statuses:", error);
        }
    };

    const fetchPhasesAndStatuses = async () => {
        try {
            console.log('Fetching phases and statuses...');
            const response = await fetch(`https://api.casedb.co/phases.php?time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            console.log('Phases and statuses response:', data);
            
            if (data.success) {
                setPhases(data.phases || []);
                setStatuses(data.statuses || []);
                console.log('Set phases:', data.phases?.length || 0, 'Set statuses:', data.statuses?.length || 0);
            } else {
                console.error('Failed to fetch phases and statuses:', data);
            }
        } catch (error) {
            console.error("Error fetching phases and statuses:", error);
        }
    };

    const postStatuses = async () => {
        setIsSaving('statuses');
        try {
            const response = await fetch(`https://api.casedb.co/statuses.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(statusData),
            });
            const data = await response.json();
            if (data.success) {
                fetchStatuses();
                setCreateNew(null);
                setStatusData({ name: "", description: "" });
            } else {
                console.error("Error posting status:", data.message);
            }
        } catch (error) {
            console.error("Error posting status:", error);
        }
        setIsSaving(null);
    };

    const updateStatus = async () => {
        setIsSaving('status');
        try {
            const response = await fetch('https://api.casedb.co/statuses.php', {
                method: 'PUT',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    id: editStatus,
                    name: statusData.name,
                    description: statusData.description
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchStatuses();
                setStatusData({ name: "", description: "" });
                setEditStatus(null);
            }
        } catch (err) {
            console.error(err);
        }
        setIsSaving(null);
    };

    const deleteStatus = async (statusId) => {
        try {
            const response = await fetch('https://api.casedb.co/statuses.php', {
                method: 'DELETE',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: statusId })
            });
            const data = await response.json();
            if (data.success) {
                fetchStatuses();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const createFolder = async () => {
        if (!folderData.name) return;
        setIsSaving('folder');
    
        const newFolder = {
            template_id: defaultTemplate.id,
            parent_folder_id: folderData.parent_folder_id || null,
            name: folderData.name,
            folder_access: folderData.folder_access,
        };
    
        try {
            const response = await fetch("https://api.casedb.co/folder-templates.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newFolder),
            });
    
            const data = await response.json();
            if (data.success) {
                fetchFolderStructure(defaultTemplate.id);
                setCreateNew(null);
                setFolderData({ name: "", parent_folder_id: null, folder_access: "Standard" });
            } else {
                alert("Failed to create folder");
            }
        } catch (error) {
            console.error("Error creating folder:", error);
        }
        setIsSaving(null);
    };

    const createVital = async () => {
        try {
            const response = await fetch('https://api.casedb.co/vitals.php', {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    name: vitalData.vital_name,
                    description: vitalData.vital_description,
                    template_id: defaultTemplate.id,
                    field_id: vitalData.vital_field ?? null
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchTemplateLayout(defaultTemplate.id);
                setVitalData({ vital_name: '', vital_description: '', vital_field: '' });
                setCreateNew(null);
            }
        } catch (err) {
            console.error(err);
        }
    }

    const updateVital = async () => {
        setIsSaving('vital');
        try {
            const response = await fetch('https://api.casedb.co/vitals.php', {
                method: 'PUT',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    id: editVital,
                    name: vitalData.vital_name,
                    description: vitalData.vital_description,
                    template_id: defaultTemplate.id,
                    field_id: vitalData.vital_field ?? null
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchTemplateLayout(defaultTemplate.id);
                setVitalData({ vital_name: '', vital_description: '', vital_field: '' });
                setEditVital(null);
            }
        } catch (err) {
            console.error(err);
        }
        setIsSaving(null);
    }

    const deleteVital = async (vitalId) => {
        try {
            const response = await fetch('https://api.casedb.co/vitals.php', {
                method: 'DELETE',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id: vitalId })
            });
            const data = await response.json();
            if (data.success) {
                fetchTemplateLayout(defaultTemplate.id);
            }
        } catch (err) {
            console.error(err);
        }
    }

    useEffect(() => {
        document.title = "Layout Editor | CaseDB";
        
        fetchTemplates();
        fetchMarketingSources();
        fetchStatuses();
        fetchPhasesAndStatuses();
        fetchMessageTemplates();
        fetchAutomationRules();
    }, []);

    // Message Templates & Automation functions
    const fetchMessageTemplates = async () => {
        try {
            const response = await fetch('https://api.casedb.co/message_automation.php?action=templates');
            const data = await response.json();
            if (data.success) {
                setMessageTemplates(data.templates);
            }
        } catch (error) {
            console.error('Error fetching message templates:', error);
        }
    };

    const fetchAutomationRules = async () => {
        try {
            const response = await fetch('https://api.casedb.co/message_automation.php?action=rules');
            const data = await response.json();
            if (data.success) {
                setAutomationRules(data.rules);
            }
        } catch (error) {
            console.error('Error fetching automation rules:', error);
        }
    };

    const createMessageTemplate = async () => {
        try {
            const response = await fetch('https://api.casedb.co/message_automation.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'create_template',
                    ...messageTemplateData
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchMessageTemplates();
                setShowMessageTemplateModal(false);
                setMessageTemplateData({
                    template_name: '',
                    description: '',
                    message_type: 'text',
                    message_content: '',
                    subject: '',
                    email_from: '',
                    attachment_s3_keys: []
                });
            }
        } catch (error) {
            console.error('Error creating message template:', error);
        }
    };

    const updateMessageTemplate = async () => {
        try {
            const response = await fetch('https://api.casedb.co/message_automation.php', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'update_template',
                    id: editingMessageTemplate.id,
                    ...messageTemplateData
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchMessageTemplates();
                setShowMessageTemplateModal(false);
                setEditingMessageTemplate(null);
            }
        } catch (error) {
            console.error('Error updating message template:', error);
        }
    };

    const deleteMessageTemplate = async (templateId) => {
        if (!window.confirm('Are you sure you want to delete this message template?')) return;
        
        try {
            const response = await fetch(`https://api.casedb.co/message_automation.php?action=template&id=${templateId}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (data.success) {
                fetchMessageTemplates();
            }
        } catch (error) {
            console.error('Error deleting message template:', error);
        }
    };

    const createAutomationRule = async () => {
        try {
            const response = await fetch('https://api.casedb.co/message_automation.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'create_rule',
                    ...automationRuleData
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchAutomationRules();
                setShowAutomationRuleModal(false);
                setAutomationRuleData({
                    rule_name: '',
                    description: '',
                    trigger_type: 'status_duration',
                    trigger_conditions: {},
                    message_template_id: '',
                    send_to_field: 'phone',
                    delay_minutes: 0
                });
            }
        } catch (error) {
            console.error('Error creating automation rule:', error);
        }
    };

    const updateAutomationRule = async () => {
        try {
            const response = await fetch('https://api.casedb.co/message_automation.php', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'update_rule',
                    id: editingAutomationRule.id,
                    ...automationRuleData
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchAutomationRules();
                setShowAutomationRuleModal(false);
                setEditingAutomationRule(null);
            }
        } catch (error) {
            console.error('Error updating automation rule:', error);
        }
    };

    const deleteAutomationRule = async (ruleId) => {
        if (!window.confirm('Are you sure you want to delete this automation rule?')) return;
        
        try {
            const response = await fetch(`https://api.casedb.co/message_automation.php?action=rule&id=${ruleId}`, {
                method: 'DELETE'
            });
            const data = await response.json();
            if (data.success) {
                fetchAutomationRules();
            }
        } catch (error) {
            console.error('Error deleting automation rule:', error);
        }
    };

    const fetchTemplates = async () => {
        try {
            const response = await fetch("https://api.casedb.co/layout.php");
            const data = await response.json();

            if (data.templates.length > 0) {
                setTemplates(data.templates.filter((template) => template.visibility !== "2"));
                const firstTemplate = data.templates.filter((template) => template.default === "1")[0];
                setDefaultTemplate(firstTemplate);
                fetchTemplateLayout(firstTemplate.id);
            }
        } catch (error) {
            console.error("Error fetching templates:", error);
        }
    };

    const fetchTemplateLayout = async (templateId) => {
        try {
            const response = await fetch(`https://api.casedb.co/sections.php?template_id=${templateId}`);
            const data = await response.json();
            
            // Sort sections with subsections appearing below their parent sections
            const sortedSections = sortSectionsWithSubsections(data.sections || []);
            setSections(sortedSections);
            
            setPhases(data.phases || []);
            setVitals(data.vitals || []);
            fetchFolderStructure(templateId);
        } catch (error) {
            console.error("Error fetching sections:", error);
        }
    };

    const fetchCurrentFields = async (sectionId = null, templateId = null) => {
        setCurrentFields([]);
        setCurrentSection(sectionId || null);

        templateId = templateId || 1;

        try {
            const response = await fetch(`https://api.casedb.co/custom_fields.php?${sectionId ? `section_id=${sectionId}&template_id=${templateId}&` : `template_id=${templateId}&`}time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            setCurrentFields(data.custom_fields || []);
            setFieldTypes(data.fields);
            setCaseTypes(data.case_types || []);
            setAllFields(data.all_custom_fields.length > 0 ? data.all_custom_fields : allFields);
        } catch (error) {
            console.error("Error fetching fields:", error);
        }
    };

    const handleTemplateChange = (e) => {
        const newTemplateId = e.target.value;
        const selectedTemplate = templates.find(template => template.id === newTemplateId);
        setDefaultTemplate(selectedTemplate);
        fetchTemplateLayout(newTemplateId);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleCheckboxChange = (name) => {
        setFormData((prevState) => ({
            ...prevState,
            [name]: prevState[name] === 1 ? 0 : 1,
        }));
    };

    const handleTemplateSubmit = async (e) => {
        setIsSaving('template');

        e.preventDefault();
        try {
            const response = await fetch("https://api.casedb.co/layout.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (data.success) {
                setCreateTemplate(false);
                fetchTemplates();
            } else {
                console.error("Error creating template:", data.message);
            }
        } catch (error) {
            console.error("Error creating template:", error);
        }
    
        setIsSaving(null);
    };

    const createNewSection = async () => {
        if (!defaultTemplate || !sectionData.name) {
            console.log("No template or section name" + defaultTemplate + Object.entries(sectionData));
            return;
        }
        const newSection = { name: sectionData.name, icon: editingSection.icon, template_id: defaultTemplate.id, description: sectionData.description, order_id: sections.length + 1 };
        setIsSaving('section');
        
        try {
            const response = await fetch("https://api.casedb.co/sections.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newSection),
            });
            const data = await response.json();
            if (data.success) {
                fetchTemplateLayout(defaultTemplate.id);
                setCreateNew(null);
                setSectionData({ name: "", description: '', icon: "Star", order_id: 0 });
                setEditingSection(null);
            }
        } catch (error) {
            console.error("Error creating section:", error);
        }
        setIsSaving(null);
    };

    const createNewPhase = async () => {
        if (!defaultTemplate || !phaseData.phase) return;
        const newPhase = { phase: phaseData.phase, description: phaseData.description, template_id: defaultTemplate.id, order_id: phaseData.order_id || phases.length + 1 };
        setIsSaving('phase');

        try {
            const response = await fetch("https://api.casedb.co/sections.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newPhase),
            });
            const data = await response.json();
            if (data.success) {
                fetchTemplateLayout(defaultTemplate.id);
                setCreateNew(null);
                setPhaseData({ phase: "", description: "", template_id: 0, order_id: 0 });
            }
        } catch (error) {
            console.error("Error creating phase:", error);
        }
        setIsSaving(null);
    };

    const updatePhase = async () => {
        if (!phaseData.phase) return;

        try {
            const response = await fetch("https://api.casedb.co/sections.php", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...phaseData, id: editPhase }),
            });
            const data = await response.json();
            if (data.success) {
                fetchTemplateLayout(defaultTemplate.id);
                setEditPhase(null);
                setPhaseData({ phase: "", description: "", template_id: 0, order_id: 0 });
            }
        } catch (error) {
            console.error("Error updating phase:", error);
        }
    };

    const deletePhase = async (phaseId) => {
        try {
            const response = await fetch(`https://api.casedb.co/sections.php?phase_id=${phaseId}`, {
                method: "DELETE",
            });
            const data = await response.json();
            if (data.success) {
                fetchTemplateLayout(defaultTemplate.id);
            } else {
                console.error("Error deleting phase:", data.message);
            }
        } catch (error) {
            console.error("Error deleting phase:", error);
        }
    };

    const addFieldToSection = async (sectionId) => {
        if (!selectedField) return;

        setIsSaving('addingfield');

        const newField = {
            section_id: sectionId,
            id: selectedField,
            template_id: defaultTemplate.id,
            order_id: currentFields.length + 1,
        };

        try {
            const response = await fetch("https://api.casedb.co/sections.php", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(newField),
            });

            const data = await response.json();
            if (data.success) {
                fetchTemplateLayout(defaultTemplate.id);
                setAddingField(null);
                setSelectedField("");
                fetchCurrentFields(sectionId, defaultTemplate.id);
                setCustomFieldData({ section_id: null, id: 0, template_id: null, order_id: 0});
            }
        } catch (error) {
            console.error("Error adding field:", error);
        }
        setIsSaving(null);
    };

    const toggleSection = (section) => {
        if (sectionExpanded !== section.id) {
            setSectionExpanded(section.id);
            fetchCurrentFields(section.id, defaultTemplate.id);
            setEditingSection({
                id: section.id,
                name: section.name,
                description: section.description,
                icon: section.icon,
                order_id: section.order_id
            })
        }
    };

    const expandSubsections = (sectionId) => {
        if (showSubsections.includes(sectionId)) {
            setShowSubsections(showSubsections.filter(id => id !== sectionId));
        } else {
            setShowSubsections([...showSubsections, sectionId]);
        }
    };

    const handleSectionPropertyChange = (e) => {
        const { name, value } = e.target;
        setEditingSection((prevState) => ({
            ...prevState, [name]: value
        }));
    };

    const handleCustomFieldChange = (e) => {
        const { name, value } = e.target;
        setCustomFieldData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleRuleChange = (fieldId, value) => {
        setCustomFieldData((prevState) => ({
            ...prevState,
            rules: {
                ...prevState.rules,
                [fieldId]: value
            }
        }));
    };

    const closeSection = () => {
        setSectionExpanded(false);
        setEditingSection(null);
        setCurrentSection(null);
    };

    const handleIconClick = () => {
        setShowIconModal(true);
    };

    const handleIconSelect = async (iconKey) => {
        handleSectionPropertyChange({ target: { name: 'icon', value: iconKey } });
        setShowIconModal(false);

        if (editingSection?.length > 1 || editingSection?.icon !== iconKey && createNew != 'section') {
            const updatedSection = { ...editingSection, icon: iconKey };
            try {
                const response = await fetch("https://api.casedb.co/sections.php", {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(updatedSection),
                });
                const data = await response.json();
                if (data.success) {
                    fetchTemplateLayout(defaultTemplate.id);
                } else {
                    console.error("Error updating section:", data.message);
                }
            } catch (error) {
                console.error("Error updating section:", error);
            }
        }
    };

    const handleSectionUpdate = async () => {
        if (editingSection) {
            try {
                const response = await fetch("https://api.casedb.co/sections.php", {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(editingSection),
                });
                const data = await response.json();
                if (data.success) {
                    fetchTemplateLayout(defaultTemplate.id);
                } else {
                    console.error("Error updating section:", data.message);
                }
            } catch (error) {
                console.error("Error updating section:", error);
            }
        }
    };

    const handleDragStart = (index) => {
        setDragging(index);
    };

    const handleDragEnter = (index) => {
        if (dragging === null) return;
        const newPhases = [...phases];
        const draggedPhase = newPhases[dragging];
        newPhases.splice(dragging, 1);
        newPhases.splice(index, 0, draggedPhase);
        setDragging(index);
        setPhases(newPhases);
    };

    const handleDragEnd = async () => {
        setDragging(null);
        const updatedPhases = phases.map((phase, index) => ({
            ...phase,
            order_id: index
        }));
        setPhases(updatedPhases);

        const movedPhase = phases[dragging];
        const originalOrderId = movedPhase.order_id;
        const targetOrderId = updatedPhases.findIndex(phase => phase.id === movedPhase.id);

        try {
            const response = await fetch("https://api.casedb.co/sections.php", {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ 
                    phases: updatedPhases,
                    moved_phase_id: movedPhase.id,
                    original_order_id: originalOrderId,
                    target_order_id: targetOrderId,
                    template_id: defaultTemplate.id
                }),
            });
            const data = await response.json();
            if (!data.success) {
                console.error("Error updating phase order:", data.message);
            }
        } catch (error) {
            console.error("Error updating phase order:", error);
        }
    };

    const handleSectionDragStart = (index) => {
        setDragging(index);
    };

    const handleSectionDragEnter = (index) => {
        if (dragging === null) return;
        const newSections = [...sections];
        const draggedSection = newSections[dragging];
        newSections.splice(dragging, 1);
        newSections.splice(index, 0, draggedSection);
        setDragging(index);
        setSections(newSections);
    };

    const handleSectionDragEnd = async () => {
        setDragging(null);
        
        // Only update order_id for parent sections (non-subsections)
        // Subsections should maintain their relationship to parent sections
        const draggedSection = sections[dragging];
        
        // If we're dragging a parent section, update its order_id based on position among other parent sections
        if (!draggedSection.subsection || draggedSection.subsection === null || draggedSection.subsection === 0) {
            const parentSections = sections.filter(s => !s.subsection || s.subsection === null || s.subsection === 0);
            const draggedParentIndex = parentSections.findIndex(s => s.id === draggedSection.id);
            const newOrderId = draggedParentIndex;
            
            const updatedSection = {
                ...draggedSection,
                order_id: newOrderId
            };

            try {
                const response = await fetch("https://api.casedb.co/sections.php", {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        ...updatedSection,
                        id: draggedSection.id
                    }),
                });
                const data = await response.json();
                if (data.success) {
                    // Refresh the sections to ensure proper sorting with subsections
                    fetchTemplateLayout(defaultTemplate.id);
                } else {
                    console.error("Error updating section order:", data.message);
                }
            } catch (error) {
                console.error("Error updating section order:", error);
            }
        } else {
            // For subsections, we might want to handle differently or not allow dragging
            // For now, let's refresh to restore the proper order
            fetchTemplateLayout(defaultTemplate.id);
        }
    };

    const fetchMarketingSources = async () => {
        try {
            const url = `https://api.casedb.co/marketing_sources.php${includeArchivedSources ? '?include_archived=true' : ''}`;
            const response = await fetch(url, { credentials: 'include' });
            const data = await response.json();
            setMarketingSources(data.marketing_sources || []);
        } catch (error) {
            console.error("Error fetching marketing sources:", error);
        }
    };

    const postMarketingSource = async (newSource) => {
        marketingSource.template_id = defaultTemplate.id;
        marketingSource.order_id = marketingSource.order_id === null ? marketingSources.length + 1 : marketingSource.order_id;
        setIsSaving('marketing');

        try {
            const response = await fetch("https://api.casedb.co/marketing_sources.php", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: 'include',
                body: JSON.stringify(marketingSource),
            });
    
            const data = await response.json();
    
            if (data.success) {
                fetchMarketingSources();
                setMarketingSource({
                    source_name: "",
                    marketing_type: 0,
                    description: "",
                    template_id: defaultTemplate.id,
                    order_id: null,
                });
                setCreateNew(null);
            } else {
                console.error("Error creating marketing source:", data.message);
            }
        } catch (error) {
            console.error("Error posting marketing source:", error);
        }
        setIsSaving(null);
    };

    const updateMarketingSource = async () => {
        setIsSaving('marketing');
        try {
            const response = await fetch('https://api.casedb.co/marketing_sources.php', {
                method: 'PUT',
                headers: { "Content-Type": "application/json" },
                credentials: 'include',
                body: JSON.stringify({
                    id: editMarketingSource,
                    source_name: marketingSource.source_name,
                    marketing_type: marketingSource.marketing_type,
                    description: marketingSource.description,
                    template_id: defaultTemplate.id,
                    order_id: marketingSource.order_id
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchMarketingSources();
                setMarketingSource({
                    source_name: "",
                    marketing_type: 0,
                    description: "",
                    template_id: defaultTemplate.id,
                    order_id: null,
                });
                setEditMarketingSource(null);
            }
        } catch (err) {
            console.error(err);
        }
        setIsSaving(null);
    };

    const deleteMarketingSource = async (sourceId) => {
        try {
            const response = await fetch('https://api.casedb.co/marketing_sources.php', {
                method: 'DELETE',
                headers: { "Content-Type": "application/json" },
                credentials: 'include',
                body: JSON.stringify({ id: sourceId })
            });
            const data = await response.json();
            if (data.success) {
                fetchMarketingSources();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const toggleArchiveMarketingSource = async (sourceId, currentArchivedStatus) => {
        const isCurrentlyArchived = Number(currentArchivedStatus) === 1;

        try {
            const response = await fetch('https://api.casedb.co/marketing_sources.php', {
                method: 'PUT',
                headers: { "Content-Type": "application/json" },
                credentials: 'include',
                body: JSON.stringify({ 
                    archive_id: sourceId,
                    archived: !isCurrentlyArchived
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchMarketingSources();
            }
        } catch (err) {
            console.error(err);
        }
    };

    const handleFieldChange = (fieldId, value) => {
        setFormData((prev) => ({
            ...prev,
            [fieldId]: value,
        }));
    };

    const renderFieldInput = (field) => {
        switch (field.field_id) {
            case 1:
                return <Text type="text" placeholder={field.name} disable/>;
            case 2:
                return <Text type="textarea" placeholder={field.name} disable/>;
            case 3:
                return <NumberInput type="currency" />;
            case 4:
                return <NumberInput type="percent" />;
            case 5:
                return <NumberInput type="number" />;
            case 6:
                return <DateInput disabled/>;
            case 7:
                return <TimeInput />;
            case 8:
                return <Contact selectedContact={formData[field.id]}/>;
            case 9:
                return (
                    <ContactList
                        selectedContact={formData[field.id]}
                        onChange={() => {}}
                    />
                );
            case 10:
                return <Dropdown
                            options={"[]"}
                        />;
            case 11:
                return <MultiSelect options={"[]"}/>;
            case 12:
                return <Boolean options={["Yes", "No", "Unknown"]} onChange={() => {}}/>;
            case 13:
                return <Subheader title={field.name} />;
            case 14:
                return <Instructions instructions={field.name} />;
            case 15:
                return <FileUpload
                    onChange={() => {}}
                />
            case 16:
                return <MultiFile
                    onChange={() => {}}
                />
            case 17:
                return (
                    <Calculation/>
                );
            case 18:
                return <DocGen
                            onChange={() => {}}
                        />;
            case 20:
                return <Deadline
                            onChange={() => {}}
                        />;
            case 22:
                return <SearchSelect
                            onChange={() => {}}
                        />;
            case 28:
                return <MatterPublicFormField field={field} disabled />;
            default:
                return null;
        }
    };

    useEffect(() => {
        fetchCurrentFields();
    }, []);

    useEffect(() => {
        fetchMarketingSources();
    }, [includeArchivedSources]);

    return (
        <div className={`firm-container ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <header>
                <div id='page-header' style={{ marginBottom: '0' }}>
                    <h2>Layout Editor</h2>
                    <select className="default-select" value={defaultTemplate?.id || ""} onChange={handleTemplateChange}>
                        <option value="" disabled>Select a template</option>
                        {templates.map((template) => (
                            <option key={template.id} value={template.id}>
                                {template.name} [{template.aka}]
                            </option>
                        ))}
                    </select>
                    <button className="action" onClick={() => setCreateTemplate(true)}>New Template</button>
                </div>
                <div id='template-header'>
                    {screenWidth < 729 ? (
                        <select className='default-select' value={selectedTemplateHeader} onChange={(e) => setSelectedTemplateHeader(Number(e.target.value))}>
                            <option value={0}>Sections</option>
                            <option value={7}>Field recommendations</option>
                            <option value={1}>Phases</option>
                            <option value={2}>Vitals</option>
                            <option value={3}>Folder Structure</option>
                            <option value={4}>Marketing Sources</option>
                            <option value={5}>Lead Statuses</option>
                            <option value={6}>Message Templates</option>
                        </select>
                    )  : (
                        <>
                            <h4 onClick={() => setSelectedTemplateHeader(0)} className={`${selectedTemplateHeader !== 0 ? 'in' : ''}active`}>Sections</h4>
                            <h4 onClick={() => setSelectedTemplateHeader(7)} className={`${selectedTemplateHeader !== 7 ? 'in' : ''}active`}>Field recommendations</h4>
                            <h4 onClick={() => setSelectedTemplateHeader(1)} className={`${selectedTemplateHeader !== 1 ? 'in' : ''}active`}>Phases</h4>
                            <h4 onClick={() => setSelectedTemplateHeader(2)} className={`${selectedTemplateHeader !== 2 ? 'in' : ''}active`}>Vitals</h4>
                            <h4 onClick={() => setSelectedTemplateHeader(3)} className={`${selectedTemplateHeader !== 3 ? 'in' : ''}active`}>Folder Structure</h4>
                            <h4 onClick={() => setSelectedTemplateHeader(4)} className={`${selectedTemplateHeader !== 4 ? 'in' : ''}active`}>Marketing Sources</h4>
                            <h4 onClick={() => setSelectedTemplateHeader(5)} className={`${selectedTemplateHeader !== 5 ? 'in' : ''}active`}>Lead Statuses</h4>
                            <h4 onClick={() => setSelectedTemplateHeader(6)} className={`${selectedTemplateHeader !== 6 ? 'in' : ''}active`}>Message Templates</h4>
                        </>
                    )}
                </div>
                <div className='template-container-header'>
                    <h4>
                        {defaultTemplate?.name}
                        {" "}
                        {selectedTemplateHeader === 0 
                            ? 'Sections' 
                                : selectedTemplateHeader === 7
                            ? 'Field recommendations'
                                : selectedTemplateHeader === 1 
                            ? 'Phases' 
                                : selectedTemplateHeader === 2 
                            ? 'Vitals' 
                                : selectedTemplateHeader === 3 
                            ? 'Folder Structure' 
                                : selectedTemplateHeader === 4 
                            ? 'Marketing Sources' 
                                : selectedTemplateHeader === 5 
                            ? 'Lead Statuses'
                                : 'Message Templates'
                        }
                    </h4>
                    <div className='template-actions'>
                        {selectedTemplateHeader === 4 && (
                            <Checkbox label="Include Archived Sources" value={includeArchivedSources} onChange={() => setIncludeArchivedSources(!includeArchivedSources)} />
                        )}
                        {selectedTemplateHeader !== 7 && <button className="action alt" onClick={() => {
                            switch (selectedTemplateHeader) {
                                case 0:
                                    setCreateNew('section');
                                    break;
                                case 1:
                                    setCreateNew('phase');
                                    break;
                                case 2:
                                    setCreateNew('vitals');
                                    break;
                                case 3:
                                    setCreateNew('folder');
                                    break;
                                case 4:
                                    setCreateNew('marketing');
                                    break;
                                case 5:
                                    setCreateNew('statuses');
                                    break;
                                case 6:
                                    setShowMessageTemplateModal(true);
                                    break;
                                default:
                                    break;
                            }
                        }}>
                            Add {
                                selectedTemplateHeader === 0 
                                    ? 'Section' 
                                    : selectedTemplateHeader === 1 
                                    ? 'Phase' 
                                    : selectedTemplateHeader === 2 
                                    ? 'Vital' 
                                    : selectedTemplateHeader === 3 
                                    ? 'Folder'
                                    : selectedTemplateHeader === 4
                                    ? "Marketing Source"
                                    : selectedTemplateHeader === 5
                                    ? 'Lead Status'
                                    : 'Message Template'
                            }
                        </button>}
                    </div>
                </div>
            </header>
            {createTemplate && (
                <Modal onClose={() => setCreateTemplate(false)}>
                    <div className="modal-content-wrapper">
                        <h4>Create New Case Template</h4>
                        <form onSubmit={handleTemplateSubmit}>
                            <div className="form-group">
                                <label htmlFor="name">Name</label>
                                <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="aka">Nickname</label>
                                <input type="text" id="aka" name="aka" value={formData.aka} onChange={handleInputChange} />
                            </div>
                            <div className="form-group">
                                <label htmlFor="description">Description</label>
                                <textarea id="description" name="description" value={formData.description || ""} onChange={handleInputChange} />
                            </div>
                            <button type="submit" className="action">Save Template</button>
                        </form>
                    </div>
                </Modal>
            )}
            <div id='template-container'>
                {selectedTemplateHeader === 7 && <FieldRecommendationSettings templateId={defaultTemplate?.id} />}
                {selectedTemplateHeader === 0 ? (
                    <div className="section-container">
                        {sections?.filter(section => (section?.subsection && showSubsections.includes(section?.subsection)) || !section?.subsection).map((section, index) => (
                            <div 
                                key={index} 
                                className={`template-section ${section.subsection ? 'subsection' : ''}`}
                                onClick={() => toggleSection(section)}
                                onDragStart={() => handleSectionDragStart(index)}
                                onDragEnter={() => handleSectionDragEnter(index)}
                                onDragEnd={handleSectionDragEnd}
                                draggable={!section.subsection} // Only allow dragging parent sections
                            >
                                {sectionExpanded !== section.id ? (
                                    <>
                                        <div className='section-header'>
                                            {IconMap[section.icon] ? React.createElement(IconMap[section.icon]) : <StarOutlineSharp />}
                                            <h4 style={{ display: 'flex', gap: '10px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>{section.name} {section.subsection && <span className='subtext'>Subsection of {sections.find(s => s.id === section.subsection)?.name}</span>}</h4>
                                            {section.built_in === 1  && <p>(Built In)</p>}
                                            {sections.reduce((count, s) => s.subsection === section.id ? count + 1 : count, 0) > 0 && (
                                                <span className='subtext'>
                                                    - {sections.reduce((count, s) => s.subsection === section.id ? count + 1 : count, 0)} subsections
                                                </span>
                                            )}
                                        </div>
                                        <p>{section.description || section.name}</p>
                                    </>
                                ) : (
                                    <>
                                        <div className='section-header far'>
                                            <h4>{section.name} Properties</h4>
                                            <Close onClick={closeSection} />
                                        </div>
                                        <div className='section-properties'>
                                            <div className='section-label'>
                                                <label htmlFor="name" className='section-floating-label'>Name & Icon</label>
                                                <div className="icon-option" onClick={handleIconClick}>
                                                    {IconMap[editingSection?.icon] ? React.createElement(IconMap[editingSection?.icon]) : <StarOutlineSharp />}
                                                </div>
                                                <input id='name' type="text" value={editingSection?.name} onChange={handleSectionPropertyChange} name="name" onBlur={handleSectionUpdate} />
                                            </div>
                                            <div className='section-label'>
                                                <label htmlFor="description" className='section-floating-label'>
                                                    Description
                                                </label>
                                                <textarea id='description' className="description-text" onChange={handleSectionPropertyChange} value={editingSection?.description ?? ""} name='description' onBlur={handleSectionUpdate} />
                                            </div>
                                            <div className='section-label'>
                                                <label htmlFor='order_id' className='section-floating-label'>
                                                    Order
                                                </label>
                                                <input id='order_id' type='number' onChange={handleSectionPropertyChange} value={editingSection?.order_id} name='order_id' onBlur={handleSectionUpdate} />
                                            </div>
                                        </div>
                                        <div className='section-fields'>
                                            <button className="action small" onClick={() => setAddingField(section.id)}>+ Field</button>
                                            {currentFields.length > 0 && currentSection === section.id &&
                                                currentFields.map((field, index) => {
                                                    if (index <= fieldLimit) {
                                                        return (
                                                            <div 
                                                                className='group-fields' 
                                                                key={field.id}
                                                                onMouseOver={() => handleMouseEnter(field.id)}
                                                                onMouseLeave={handleMouseLeave}
                                                            >
                                                                <h4>{field.name}</h4>
                                                                {fieldTypes.map((type, index) => {
                                                                    if (Number(type.id) === Number(field.field_id)) {
                                                                        return (
                                                                            <span className='subtext' key={index}>{type.name}</span>
                                                                        )
                                                                    }
                                                                })}
                                                                {field.id === hoveredField && (
                                                                    <div className='form-group middle' key={field.id}>
                                                                        {renderFieldInput(field)}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )
                                                    }
                                                })
                                            }
                                            {currentFields.length > fieldLimit ? (
                                                <button className='action alt'onClick={() => setFieldLimit(currentFields.length)}>Show More Fields</button>
                                            ) : (currentFields.length > 0 && currentFields.length > 4 && 
                                                <button className='action alt'onClick={() => setFieldLimit(4)}>Show Less Fields</button>
                                            )}
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (selectedTemplateHeader === 1 ? (
                        <div className="phase-container">
                            <div className='phase-list'>
                                {phases.map((phase, index) => (
                                    <div 
                                        key={index} 
                                        className="template-phase far" 
                                        onDragStart={() => handleDragStart(index)}
                                        onDragEnter={() => handleDragEnter(index)}
                                        onDragEnd={handleDragEnd}
                                        draggable
                                    >
                                        <div className='phase-title' title={phase.description}>
                                            <h4>{phase.phase}</h4>
                                            {phase.phase === "Archived" ? <Archive size="15" color="var(--hover-color)"/> : <></>}
                                        </div>
                                        <div className='phase-actions'>
                                            <Edit 
                                                onClick={() => {
                                                        setEditPhase(phase.id)
                                                        setPhaseData({ phase: phase.phase, description: phase.description, order_id: phase.order_id })
                                                    }
                                                }
                                                size={22}
                                            />
                                            <Trash2 
                                                onClick={() => {
                                                        if (window.confirm(`Are you sure you want to delete the ${phase.phase} phase?`)) {
                                                            deletePhase(phase.id);
                                                        }
                                                    }
                                            }
                                                size={22}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (selectedTemplateHeader === 2 ? (
                        <div className='vitals-container'>
                            <div className='vitals-list'>
                                <Vital setEditVital={setEditVital} setVitalData={setVitalData} deleteVital={deleteVital} vital='Case Type' description='The type of case being managed. Value assigned during lead creation.' />
                                {vitals.map((vital, index) => (
                                        <Vital setEditVital={setEditVital} setVitalData={setVitalData} deleteVital={deleteVital} vital={vital.name} description={vital.description} edit={true} />
                                ))}
                            </div>
                        </div>
                    ) : (selectedTemplateHeader === 3 ? (
                        <FolderTreeManager 
                            templateId={defaultTemplate?.id} 
                            folders={folders}
                        />
                    ) : (selectedTemplateHeader === 4 ? (
                        <div className='marketing-source-container'>
                            {marketingSources.map((source, index) => (
                                <div key={index} className={`marketing-source ${Number(source.archived) === 1 ? 'archived' : ''}`}>
                                    <div className='marketing-source-content'>
                                        <h4>{source.source_name}</h4>
                                        <p>{source.description}</p>
                                        <p>({source.marketing_type === '1' ? "Marketing Source" : source.marketing_type === '2' ? "Contact Source" : "Referral Source"})</p>
                                    </div>
                                    <div className='marketing-source-actions'>
                                        <Edit 
                                            onClick={() => {
                                                setEditMarketingSource(source.id);
                                                setMarketingSource({
                                                    source_name: source.source_name,
                                                    marketing_type: source.marketing_type,
                                                    description: source.description,
                                                    template_id: source.template_id,
                                                    order_id: source.order_id
                                                });
                                            }}
                                            size={22}
                                        />
                                        {Number(source.archived) === 1 ? (
                                            <ArchiveX 
                                                onClick={() => toggleArchiveMarketingSource(source.id, source.archived)}
                                                title="Unarchive"
                                                size={22}
                                            />
                                        ) : (
                                            <Archive 
                                                onClick={() => toggleArchiveMarketingSource(source.id, source.archived)}
                                                title="Archive"
                                                size={22}
                                            />
                                        )}
                                        <Trash2
                                            onClick={() => {
                                                if (window.confirm(`Are you sure you want to delete the "${source.source_name}" marketing source?`)) {
                                                    deleteMarketingSource(source.id);
                                                }
                                            }}
                                            size={22}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (selectedTemplateHeader === 5 ? (
                        <div className='lead-status-container'>
                            {statuses.map((status, index) => status.substatus === null && (
                                <div 
                                    key={index} 
                                    className={`lead-status${statusExpanded === status.id ? ' stacked' : ''}`}
                                    onClick={() => {
                                            setStatusExpanded(statusExpanded === status.id || statuses.reduce((acc, s) => acc += s.substatus === status.id ? 1 : 0, 0) === 0 ? null : status.id);
                                        }
                                    }
                                >
                                    <div className='status-content'>
                                        <h4>{status.name}</h4>
                                        <p>{status.description}</p>
                                        {statusExpanded !== status.id && <p>Substatuses: {statuses.reduce((acc, s) => acc += s.substatus === status.id ? 1 : 0, 0)}</p>}
                                        {statusExpanded === status.id && (
                                            <div className='sub-statuses'>
                                                Substatuses:
                                                {statuses.map((substatus, index) => substatus.substatus === status.id && (
                                                    <p className='subtext sub-status' key={index}>
                                                        {substatus.name}
                                                        <span>
                                                            {substatus.description ? `(${substatus.description})` : null} 
                                                        </span>
                                                    </p>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div className='status-actions'>
                                        <Edit onClick={(e) => {
                                            e.stopPropagation();
                                            setEditStatus(status.id);
                                            setStatusData({
                                                name: status.name,
                                                description: status.description
                                            });
                                        }} />
                                        <Trash2 onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm(`Are you sure you want to delete the "${status.name}" status?`)) {
                                                deleteStatus(status.id);
                                            }
                                        }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (selectedTemplateHeader === 6 ? (
                    <div className='message-templates-container'>
                        <div className='message-templates-section'>
                            <h3>Message Templates</h3>
                            <div className='message-templates-list'>
                                {messageTemplates.map((template, index) => (
                                    <div key={index} className='message-template'>
                                        <div className='template-content'>
                                            <div className='template-content-header'>
                                                <h4>{template.template_name}</h4>
                                                <p className='subtext'>{template.description}</p>
                                            </div>
                                            <div className='template-preview'>
                                                <strong>Message:</strong> {template.message_content.substring(0, 250)}
                                                {template.message_content.length > 250 && '...'}
                                            </div>
                                        </div>
                                        <div className='message-template-actions'>
                                            <span className='message-template-action'>
                                                <Edit
                                                    onClick={() => {
                                                        setEditingMessageTemplate(template);
                                                        setMessageTemplateData({
                                                            template_name: template.template_name,
                                                            description: template.description,
                                                            message_content: template.message_content,
                                                            subject: template.subject,
                                                            attachment_s3_keys: template.attachment_s3_keys || []
                                                        });
                                                        setShowMessageTemplateModal(true);
                                                        
                                                    }}
                                                    size={18}
                                                />
                                            </span>
                                            <span className='message-template-action'>
                                                <Trash2 
                                                    onClick={() => deleteMessageTemplate(template.id)}
                                                    size={18} 
                                                />
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div className='automation-rules-section'>
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px'}}>
                                <h3>Automation Rules</h3>
                                <button 
                                    className="action alt" 
                                    onClick={() => {
                                        setAutomationRuleData({
                                            rule_name: '',
                                            description: '',
                                            trigger_type: 'case_created',
                                            trigger_conditions: {},
                                            message_template_id: '',
                                            send_to_field: 'email',
                                            delay_minutes: 0
                                        });
                                        setShowAutomationRuleModal(true);
                                    }}
                                >
                                    Add Automation Rule
                                </button>
                            </div>
                            <div className='automation-rules-list'>
                                {automationRules.map((rule, index) => (
                                    <div key={index} className='automation-rule'>
                                        <div className='rule-content'>
                                            <div className='rule-content-header'>
                                                <h4>{rule.rule_name}</h4>
                                                <p>{rule.description}</p>
                                            </div>
                                            <div className='rule-details'>
                                                <span className='trigger-type'>{rule.trigger_type.replace('_', ' ')}</span>
                                                <span className='template-name'>→ {rule.message_template_name}</span>
                                                <span className='send-to'>to {rule.send_to_field}</span>
                                            </div>
                                            <div className='rule-stats'>
                                                <span>Pending: {rule.pending_count || 0}</span>
                                                <span>Sent: {rule.sent_count || 0}</span>
                                            </div>
                                        </div>
                                        <div className='rule-actions'>
                                            <span className='rule-action'>
                                                <Edit 
                                                    onClick={() => {
                                                        setEditingAutomationRule(rule);
                                                        setAutomationRuleData({
                                                            rule_name: rule.rule_name,
                                                            description: rule.description,
                                                            trigger_type: rule.trigger_type,
                                                            trigger_conditions: rule.trigger_conditions,
                                                            message_template_id: rule.message_template_id,
                                                            send_to_field: rule.send_to_field,
                                                            delay_minutes: rule.delay_minutes
                                                        });
                                                        setShowAutomationRuleModal(true);
                                                    }} 
                                                    size={18}
                                                />
                                            </span>
                                            <span className='rule-action'>
                                                <Trash2 onClick={() => deleteAutomationRule(rule.id)} size={18} />
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : null))))))
                }
            </div>
            {addingField && (
                <Modal onClose={() => setAddingField(null)}>
                    <div className="modal-content-wrapper">
                        <h4>Add Field to Section</h4>
                        <div className="form-group">
                            <label htmlFor='field_type'>Select Field</label>
                            <select 
                                value={selectedField} 
                                onChange={(e) => setSelectedField(e.target.value)}
                                className="default-select"
                                name='field_type'
                            >
                                <option value="">Select a Field</option>
                                {currentFields.filter((field) => field.section_id === 0).map((field) => (
                                    <option key={field.id} value={field.id}>{field.name}</option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="order_id">Order</label>
                            <input 
                                type="number" 
                                id="order_id" 
                                name="order_id" 
                                value={customFieldData.order_id || currentFields.length + 1} 
                                onChange={handleCustomFieldChange} 
                            />
                        </div>
                        {selectedField && <div className="form-group">
                            <label htmlFor="visibility-rule">Visibility</label>
                            <select 
                                value={customFieldData.rules.visibility} 
                                onChange={(e) => handleRuleChange('visibility', e.target.value)}
                                className="default-select"
                                name='visibility-rule'
                            >
                                <option value="">Select Visibility</option>
                                <option value="1">Show</option>
                                <option value="0">Hide</option>
                            </select>
                        </div>}
                        {customFieldData.rules.visibility && <div className="form-group">
                            <select 
                                value={customFieldData.rules.when} 
                                onChange={(e) => handleRuleChange('when', e.target.value)}
                                className="default-select"
                            >
                                <option value="0">Always</option>
                                {currentFields.map(field => (
                                    <option key={field.id} value={field.id}>When "{field.name}"</option>
                                ))}
                            </select>
                        </div>}
                        <button className="action" onClick={() => addFieldToSection(addingField)}>Save Field</button>
                    </div>
                </Modal>
            )}
            {createNew && (
                <Modal onClose={() => setCreateNew(null)}>
                    {createNew === 'section' ? (
                        <div className="modal-content-wrapper">
                            <h4>Create New Section</h4>
                            <div className='form-group'>
                                <label htmlFor='name'>Section Name & Icon</label>
                                <div className='section-label max'>
                                    <div className="icon-option" onClick={handleIconClick}>
                                        {IconMap[editingSection?.icon] ? React.createElement(IconMap[editingSection?.icon]) : <StarOutlineSharp />}
                                    </div>
                                    <input
                                        type="text" 
                                        placeholder="Section Name" 
                                        name="name" 
                                        value={sectionData.name}
                                        onChange={(e) => setSectionData({ ...sectionData, name: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor='description'>Description</label>
                                <textarea placeholder="Section Description" name='description' value={sectionData.description} onChange={(e) => setSectionData({...sectionData, description: e.target.value })}/>
                            </div>
                            <button className="action" onClick={createNewSection}>{isSaving === 'section' ? <Loader2 className="spinner" /> : "Save Section"}</button>
                        </div>
                    ) : createNew === 'phase' ? (
                        <div className='modal-content-wrapper'>
                            <h4>Create New Phase</h4>
                            <div className='form-group'>
                                <label htmlFor='phase'>Phase Name</label>
                                <input type="text" placeholder="Phase Name" value={phaseData.phase} onChange={(e) => setPhaseData({ ...phaseData, phase: e.target.value })}/>
                            </div>
                            <div className='form-group'>
                                <label htmlFor='description'>Description</label>
                                <textarea placeholder="Phase Description" name='description' value={phaseData.description} onChange={(e) => setPhaseData({ ...phaseData, description: e.target.value })}/>
                            </div>
                            <div className='form-group'>
                                <label htmlFor='order_id'>Order</label>
                                <input type='number' placeholder='Order' value={phaseData.order_id} onChange={(e) => setPhaseData({ ...phaseData, order_id: e.target.value })}/>
                            </div>
                            <button className="action" onClick={createNewPhase}>Save Phase</button>
                        </div>
                    ) : (createNew === 'vitals' ? (
                        <div className='modal-content-wrapper'>
                            <h4>Create New Vital</h4>
                            <div className='form-group'>
                                <label htmlFor='vital_name'>Vital Name</label>
                                <input type="text" id='vital_name' onChange={(e) => setVitalData({ ...vitalData, vital_name: e.target.value })} value={vitalData.vital_name} name='vital_name' placeholder="Vital Name"/>
                            </div>
                            <div className='form-group'>
                                <label htmlFor='vital_description'>Vital Description</label>
                                <textarea id='vital_description' onChange={(e) => setVitalData({ ...vitalData, vital_description: e.target.value })} value={vitalData.vital_description} name='vital_description' placeholder="Vital Description"/>
                            </div>
                            <div className='form-group'>
                                <label htmlFor='vital_field'>Vital Field</label>
                                <select className='default-select' onChange={(e) => setVitalData({ ...vitalData, vital_field: e.target.value })} value={vitalData.vital_field} >
                                    <option value=''>Select Vital Field</option>
                                    {currentFields.map(field => (
                                        <option key={field.id} value={field.id}>{field.name}</option>
                                    ))}
                                </select>
                            </div>
                            <button className='action alt' onClick={createVital}>Create</button>
                        </div>
                    ) : (createNew === 'folder' ? (
                            <div className='modal-content-wrapper'>
                                <h4>Create New Folder</h4>
                                <div className='form-group'>
                                    <label htmlFor='name'>Folder Name</label>
                                    <input type="text" placeholder="Folder Name" value={folderData.name} onChange={(e) => setFolderData({ ...folderData, name: e.target.value })}/>
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='parent_folder_id'>Parent Folder</label>
                                    <select
                                        className="default-select"
                                        value={folderData.parent_folder_id || ""}
                                        onChange={(e) => setFolderData({ ...folderData, parent_folder_id: e.target.value })}
                                    >
                                        <option value={0}>Root Folder</option>
                                        {folders.map(folder => (
                                            <option key={folder.id} value={folder.id}>{folder.name}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='folder_access'>Folder Access</label>
                                    <select
                                        className="default-select"
                                        value={folderData.folder_access}
                                        onChange={(e) => setFolderData({ ...folderData, folder_access: e.target.value })}
                                    >
                                        <option value="Standard">Standard</option>
                                        <option value="Protected">Protected</option>
                                    </select>
                                </div>
                                <button className="action" onClick={createFolder}>{isSaving === 'folder' ? <Loader2 className='spinner'/> : "Folder"}</button>
                            </div>
                        ) : (createNew === 'marketing' ? (
                            <div className='modal-content-wrapper'>
                                <h4>Create New Marketing Source</h4>
                                <div className='form-group'>
                                    <label htmlFor='source_name'>Source Name</label>
                                    <input type="text" id='source_name' onChange={(e) => setMarketingSource({ ...marketingSource, source_name: e.target.value })} value={marketingSource.source_name} name='source_name' placeholder="Source Name"/>
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='description'>Description</label>
                                    <textarea placeholder="Source Description"  onChange={(e) => setMarketingSource({ ...marketingSource, description: e.target.value })} value={marketingSource.description} />
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='source_type'>Source Type</label>
                                    <select className='default-select'  onChange={(e) => setMarketingSource({ ...marketingSource, marketing_type: e.target.value })} value={marketingSource.marketing_type} >
                                        <option value=''>Select Source Type</option>
                                        <option value='1'>Marketing Source</option>
                                        <option value='2'>Contact Source</option>
                                        <option value='3'>Referral Source</option>
                                    </select>
                                </div>
                                <div className='form-group'>
                                    <label htmlFor='order_id'>Order</label>
                                    <input type='number' placeholder='Order' onChange={(e) => setMarketingSource({ ...marketingSource, order_id: e.target.value})}/>
                                </div>
                                <button className="action" onClick={postMarketingSource}>Save New Source</button>
                            </div>
                        ) : (createNew === 'statuses' ? (
                            <div className='modal-content-wrapper'>
                                <h4>Create New Lead Status</h4>
                                <div className="form-group">
                                    <label htmlFor='status_name'>Status Name</label>
                                    <input type="text" placeholder="Status Name" value={statusData.name} onChange={(e) => setStatusData({ ...statusData, name: e.target.value })}/>
                                </div>
                                <div className="form-group">
                                    <label htmlFor='status_name'>Status Description</label>
                                    <textarea placeholder="Status Description" value={statusData.description} onChange={(e) => setStatusData({ ...statusData, description: e.target.value })}/>
                                </div>
                                <button className="action" onClick={postStatuses}>Save Status</button>
                            </div>
                        ) : <></>
                    ))))}
                </Modal>
            )}
            {showIconModal && (
                <Modal onClose={() => setShowIconModal(false)}>
                    <div className="modal-content-wrapper">
                        <div className="icon-selection">
                            {Object.keys(IconMap).map((iconKey) => (
                                <div
                                    key={iconKey}
                                    className={`icon-option ${editingSection?.icon === iconKey ? 'selected' : ''}`}
                                    onClick={() => handleIconSelect(iconKey)}
                                    title={iconKey}
                                >
                                    {React.createElement(IconMap[iconKey])}
                                </div>
                            ))}
                        </div>
                    </div>
                </Modal>
            )}
            {editPhase !== null && (
                <Modal onClose={() => {setEditPhase(null); setPhaseData({ phase: "", description: "", template_id: 0, order_id: 0 })}}>
                    <div className="modal-content-wrapper">
                        <h4>Edit Phase</h4>
                        <div className='form-group'>
                            <label htmlFor='phase'>Phase Name</label>
                            <input type="text" placeholder="Phase Name" value={phaseData.phase} onChange={(e) => setPhaseData({ ...phaseData, phase: e.target.value })}/>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='description'>Description</label>
                            <textarea placeholder="Phase Description" name='description' value={phaseData.description} onChange={(e) => setPhaseData({ ...phaseData, description: e.target.value })}/>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='order_id'>Order</label>
                            <input type='number' placeholder='Order' value={phaseData.order_id} onChange={(e) => setPhaseData({ ...phaseData, order_id: e.target.value })}/>
                        </div>
                        <button className="action" onClick={updatePhase}>Save Phase</button>
                    </div>
                </Modal>  
            )}
            {editVital !== null && (
                <Modal onClose={() => {setEditVital(null); setVitalData({ vital_name: '', vital_description: '', vital_field: '' })}}>
                    <div className="modal-content-wrapper">
                        <h4>Edit Vital</h4>
                        <div className='form-group'>
                            <label htmlFor='vital_name'>Vital Name</label>
                            <input type="text" id='vital_name' value={vitalData.vital_name} onChange={(e) => setVitalData({ ...vitalData, vital_name: e.target.value })} placeholder="Vital Name"/>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='vital_description'>Vital Description</label>
                            <textarea id='vital_description' value={vitalData.vital_description} onChange={(e) => setVitalData({ ...vitalData, vital_description: e.target.value })} placeholder="Vital Description"/>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='vital_field'>Vital Field</label>
                            <select className='default-select' onChange={(e) => setVitalData({ ...vitalData, vital_field: e.target.value })} value={vitalData.vital_field || ''} >
                                <option value=''>Select Vital Field</option>
                                {allFields.map(field => (
                                    <option key={field.id} value={field.id}>{field.name}</option>
                                ))}
                            </select>
                        </div>
                        <button className="action" onClick={updateVital}>{isSaving === 'vital' ? <Loader2 className="spinner" /> : "Save Vital"}</button>
                    </div>
                </Modal>
            )}
            {editMarketingSource !== null && (
                <Modal onClose={() => {setEditMarketingSource(null); setMarketingSource({ source_name: "", marketing_type: 0, description: "", template_id: 0, order_id: null })}}>
                    <div className="modal-content-wrapper">
                        <h4>Edit Marketing Source</h4>
                        <div className='form-group'>
                            <label htmlFor='source_name'>Source Name</label>
                            <input type="text" id='source_name' onChange={(e) => setMarketingSource({ ...marketingSource, source_name: e.target.value })} value={marketingSource.source_name} placeholder="Source Name"/>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='description'>Description</label>
                            <textarea placeholder="Source Description" onChange={(e) => setMarketingSource({ ...marketingSource, description: e.target.value })} value={marketingSource.description} />
                        </div>
                        <div className='form-group'>
                            <label htmlFor='source_type'>Source Type</label>
                            <select className='default-select' onChange={(e) => setMarketingSource({ ...marketingSource, marketing_type: e.target.value })} value={marketingSource.marketing_type} >
                                <option value=''>Select Source Type</option>
                                <option value='1'>Marketing Source</option>
                                <option value='2'>Contact Source</option>
                                <option value='3'>Referral Source</option>
                            </select>
                        </div>
                        <div className='form-group'>
                            <label htmlFor='order_id'>Order</label>
                            <input type='number' placeholder='Order' onChange={(e) => setMarketingSource({ ...marketingSource, order_id: e.target.value})} value={marketingSource.order_id || ''}/>
                        </div>
                        <button className="action" onClick={updateMarketingSource}>{isSaving === 'marketing' ? <Loader2 className="spinner" /> : "Save Marketing Source"}</button>
                    </div>
                </Modal>
            )}
            {editStatus !== null && (
                <Modal onClose={() => {setEditStatus(null); setStatusData({ name: "", description: "" })}}>
                    <div className="modal-content-wrapper">
                        <h4>Edit Lead Status</h4>
                        <div className="form-group">
                            <label htmlFor='status_name'>Status Name</label>
                            <input type="text" placeholder="Status Name" value={statusData.name} onChange={(e) => setStatusData({ ...statusData, name: e.target.value })}/>
                        </div>
                        <div className="form-group">
                            <label htmlFor='status_description'>Status Description</label>
                            <textarea placeholder="Status Description" value={statusData.description} onChange={(e) => setStatusData({ ...statusData, description: e.target.value })}/>
                        </div>
                        <button className="action" onClick={updateStatus}>{isSaving === 'status' ? <Loader2 className="spinner" /> : "Save Status"}</button>
                    </div>
                </Modal>
            )}
            {showMessageTemplateModal && (
                <Modal onClose={() => {
                    setShowMessageTemplateModal(false);
                    setEditingMessageTemplate(null);
                    setMessageTemplateData({
                        template_name: '',
                        description: '',
                        message_type: 'text',
                        message_content: '',
                        subject: '',
                        email_from: '',
                        attachment_s3_keys: []
                    });
                }}>
                    <div className="modal-content-wrapper">
                        <h4>{editingMessageTemplate ? 'Edit' : 'Create'} Message Template</h4>
                        <div className="form-group">
                            <label htmlFor='template_name'>Template Name</label>
                            <input 
                                type="text" 
                                id='template_name' 
                                placeholder="Template Name" 
                                value={messageTemplateData.template_name || ''} 
                                onChange={(e) => setMessageTemplateData({ ...messageTemplateData, template_name: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor='description'>Description</label>
                            <textarea 
                                id='description'
                                placeholder="Template Description" 
                                value={messageTemplateData.description || ''} 
                                onChange={(e) => setMessageTemplateData({ ...messageTemplateData, description: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor='message_type'>Message Type</label>
                            <select 
                                className='default-select' 
                                id='message_type'
                                value={messageTemplateData.message_type || 'text'} 
                                onChange={(e) => setMessageTemplateData({ ...messageTemplateData, message_type: e.target.value })}
                            >
                                <option value='text'>Text Message</option>
                                <option value='email'>Email</option>
                            </select>
                        </div>
                        {messageTemplateData.message_type === 'email' && (
                            <>
                                <div className="form-group">
                                    <label htmlFor='subject'>Email Subject</label>
                                    <input 
                                        type="text" 
                                        id='subject'
                                        placeholder="Email Subject (use {{placeholders}} for dynamic content)" 
                                        value={messageTemplateData.subject || ''} 
                                        onChange={(e) => setMessageTemplateData({ ...messageTemplateData, subject: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor='email_from'>From Email</label>
                                    <select 
                                        className='default-select' 
                                        id='email_from'
                                        value={messageTemplateData.email_from || ''} 
                                        onChange={(e) => setMessageTemplateData({ ...messageTemplateData, email_from: e.target.value })}
                                    >
                                        <option value=''>Auto-select based on case/lead</option>
                                        <option value='communication@casedb.co'>communication@casedb.co</option>
                                        <option value='intake@casedb.co'>intake@casedb.co</option>
                                    </select>
                                </div>
                            </>
                        )}
                        <div className="form-group">
                            <label htmlFor='message_content'>Message Content</label>
                            <textarea 
                                id='message_content'
                                placeholder="Message content (use {{contact_name}}, {{case_name}}, etc. for dynamic content)" 
                                value={messageTemplateData.message_content || ''} 
                                onChange={(e) => setMessageTemplateData({ ...messageTemplateData, message_content: e.target.value })}
                                rows="4"
                            />
                        </div>
                        <div className="form-group">
                            <small>Available placeholders: {`{{contact_name}}, {{contact_first_name}}, {{case_name}}, {{case_id}}, {{lead_id}}, {{lead_status}}, {{case_phase}}, {{primary_attorney}}`}</small>
                        </div>
                        <button className="action" onClick={editingMessageTemplate ? updateMessageTemplate : createMessageTemplate}>
                            {isSaving === 'message_template' ? <Loader2 className="spinner" /> : (editingMessageTemplate ? 'Update' : 'Create') + ' Template'}
                        </button>
                    </div>
                </Modal>
            )}
            {showAutomationRuleModal && (
                <Modal
                    title='Automation Rule'
                    onClose={() => {
                        setShowAutomationRuleModal(false);
                        setEditingAutomationRule(null);
                        setAutomationRuleData({
                            rule_name: '',
                            description: '',
                            trigger_type: 'case_created',
                            trigger_conditions: {},
                            action_type: 'message',
                            message_template_id: '',
                            notification_target: 'contact',
                            target_config: {},
                            send_to_field: 'email',
                            delay_minutes: 0
                        });
                    }}
                    header={
                        <div className='modal-header-actions'>
                            <button className="action destroy" onClick={() => {
                                setShowAutomationRuleModal(false);
                                setEditingAutomationRule(null);
                                setAutomationRuleData({
                                    rule_name: '',
                                    description: '',
                                    trigger_type: 'case_created',
                                    trigger_conditions: {},
                                    action_type: 'message',
                                    message_template_id: '',
                                    notification_target: 'contact',
                                    target_config: {},
                                    send_to_field: 'email',
                                    delay_minutes: 0
                                });
                            }}>Cancel</button>
                            <button className="action" onClick={editingAutomationRule ? updateAutomationRule : createAutomationRule}>
                                {isSaving === 'automation_rule' ? <Loader2 className="spinner" /> : (editingAutomationRule ? 'Update' : 'Create') + ' Rule'}
                            </button>
                        </div>
                    }
                >
                    <div className="modal-content-wrapper">
                        <div className="form-group">
                            <label className='subtext' htmlFor='rule_name'>Rule Name</label>
                            <input 
                                type="text" 
                                id='rule_name'
                                placeholder="Rule Name" 
                                value={automationRuleData.rule_name || ''} 
                                onChange={(e) => setAutomationRuleData({ ...automationRuleData, rule_name: e.target.value })}
                            />
                        </div>
                        <div className="form-group">
                            <label className='subtext' htmlFor='rule_description'>Description</label>
                            <textarea 
                                id='rule_description'
                                placeholder="Rule Description" 
                                value={automationRuleData.description || ''} 
                                onChange={(e) => setAutomationRuleData({ ...automationRuleData, description: e.target.value })}
                            />
                        </div>
                        <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                            <label className='subtext' htmlFor='trigger_type'>Trigger Type</label>
                            <div className="select-wrapper" style={{ width: '100%' }}>
                                <select 
                                    className='filter-select' 
                                    id='trigger_type'
                                    value={automationRuleData.trigger_type || 'case_created'} 
                                    onChange={(e) => {
                                        const newTriggerType = e.target.value;
                                        const updatedData = { ...automationRuleData, trigger_type: newTriggerType };
                                        
                                        // Set default values for status_duration trigger
                                        if (newTriggerType === 'status_duration') {
                                            updatedData.trigger_conditions = {
                                                ...automationRuleData.trigger_conditions,
                                                duration_type: 'lead_status'
                                            };
                                        }
                                        
                                        setAutomationRuleData(updatedData);
                                    }}
                                    style={{ width: '100%' }}
                                >
                                    <option value='case_created'>Case Created</option>
                                    <option value='lead_created'>Lead Created</option>
                                    <option value='lead_status_changed'>Lead Status Changed</option>
                                    <option value='case_phase_changed'>Case Phase Changed</option>
                                    <option value='status_duration'>Status Duration</option>
                                    <option value='task_notification'>Task Notification Check</option>
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                        {automationRuleData.trigger_type === 'status_duration' && (
                            <>
                                <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                                    <label className='subtext' htmlFor='duration_type'>Duration Type</label>
                                    <div className="select-wrapper" style={{ width: '100%' }}>
                                        <select 
                                            id='duration_type'
                                            className='filter-select'
                                            value={automationRuleData.trigger_conditions?.duration_type || 'lead_status'} 
                                            onChange={(e) => setAutomationRuleData({ 
                                                ...automationRuleData, 
                                                trigger_conditions: { 
                                                    ...automationRuleData.trigger_conditions, 
                                                    duration_type: e.target.value,
                                                    status_id: '', // Reset selection when type changes
                                                    phase_id: ''   // Reset selection when type changes
                                                } 
                                            })}
                                            style={{ width: '100%' }}
                                        >
                                            <option value='lead_status'>Lead Status Duration</option>
                                            <option value='case_phase'>Case Phase Duration</option>
                                        </select>
                                        <ChevronDown size={16} className="select-chevron" />
                                    </div>
                                </div>
                                
                                {automationRuleData.trigger_conditions?.duration_type === 'lead_status' && (
                                    <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                                        <label className='subtext' htmlFor='status_id'>Lead Status</label>
                                        <div className="select-wrapper" style={{ width: '100%' }}>
                                            <select 
                                                id='status_id'
                                                className='filter-select'
                                                value={automationRuleData.trigger_conditions?.status_id || ''} 
                                                onChange={(e) => setAutomationRuleData({ 
                                                    ...automationRuleData, 
                                                    trigger_conditions: { 
                                                        ...automationRuleData.trigger_conditions, 
                                                        status_id: e.target.value
                                                    } 
                                                })}
                                                style={{ width: '100%' }}
                                            >
                                                <option value=''>Select Status</option>
                                                {statuses && statuses.length > 0 ? statuses.map(status => (
                                                    <option key={status.id} value={status.id}>{status.name}</option>
                                                )) : <option disabled>Loading statuses...</option>}
                                            </select>
                                            <ChevronDown size={16} className="select-chevron" />
                                        </div>
                                        <small style={{ color: '#666' }}>Available statuses: {statuses ? statuses.length : 0}</small>
                                    </div>
                                )}
                                
                                {automationRuleData.trigger_conditions?.duration_type === 'case_phase' && (
                                    <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                                        <label className='subtext' htmlFor='phase_id'>Case Phase</label>
                                        <div className='select-wrapper' style={{ width: '100%' }}>
                                            <select 
                                                id='phase_id'
                                                className='filter-select'
                                                value={automationRuleData.trigger_conditions?.phase_id || ''} 
                                                onChange={(e) => setAutomationRuleData({ 
                                                    ...automationRuleData, 
                                                    trigger_conditions: { 
                                                        ...automationRuleData.trigger_conditions, 
                                                        phase_id: e.target.value
                                                    } 
                                                })}
                                                style={{ width: '100%' }}
                                            >
                                                <option value=''>Select Phase</option>
                                                {phases && phases.length > 0 ? phases.map(phase => (
                                                    <option key={phase.id} value={phase.id}>{phase.phase}</option>
                                                )) : <option disabled>Loading phases...</option>}
                                            </select>
                                            <ChevronDown size={16} className="select-chevron" />
                                        </div>
                                        <small style={{ color: '#666' }}>Available phases: {phases ? phases.length : 0}</small>
                                    </div>
                                )}
                                
                                <div className="form-group">
                                    <label className='subtext' htmlFor='duration_days'>Duration (Days)</label>
                                    <input 
                                        type="text" 
                                        id='duration_days'
                                        placeholder="7,14,30 (comma-separated for multiple milestones)" 
                                        value={automationRuleData.trigger_conditions?.duration_days || ''} 
                                        onChange={(e) => setAutomationRuleData({ 
                                            ...automationRuleData, 
                                            trigger_conditions: { 
                                                ...automationRuleData.trigger_conditions, 
                                                duration_days: e.target.value
                                            } 
                                        })}
                                    />
                                    <small style={{ color: '#666', display: 'block', marginTop: '5px' }}>
                                        Enter single value (e.g., "7") or comma-separated values (e.g., "7,14,30") to send the same message at multiple duration milestones
                                    </small>
                                </div>
                            </>
                        )}
                        {automationRuleData.trigger_type === 'lead_status_changed' && (
                            <div className="form-group">
                                <label className='subtext' htmlFor='to_status'>To Status (comma-separated IDs)</label>
                                <input 
                                    type="text" 
                                    id='to_status'
                                    placeholder="1,2,3 (status IDs to trigger on)" 
                                    value={automationRuleData.trigger_conditions?.to_status?.join(',') || ''} 
                                    onChange={(e) => setAutomationRuleData({ 
                                        ...automationRuleData, 
                                        trigger_conditions: { 
                                            ...automationRuleData.trigger_conditions, 
                                            to_status: e.target.value.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
                                        } 
                                    })}
                                />
                            </div>
                        )}
                        {automationRuleData.trigger_type === 'case_phase_changed' && (
                            <div className="form-group">
                                <label className='subtext' htmlFor='to_phase'>To Phase (comma-separated IDs)</label>
                                <input 
                                    type="text" 
                                    id='to_phase'
                                    placeholder="1,2,3 (phase IDs to trigger on)" 
                                    value={automationRuleData.trigger_conditions?.to_phase?.join(',') || ''} 
                                    onChange={(e) => setAutomationRuleData({ 
                                        ...automationRuleData, 
                                        trigger_conditions: { 
                                            ...automationRuleData.trigger_conditions, 
                                            to_phase: e.target.value.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id))
                                        } 
                                    })}
                                />
                            </div>
                        )}
                        {automationRuleData.trigger_type === 'task_notification' && (
                            <>
                                <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                                    <label className='subtext' htmlFor='check_table'>Check Table</label>
                                    <div className="select-wrapper" style={{ width: '100%' }}>
                                        <select 
                                            className='filter-select' 
                                            id='check_table'
                                            value={automationRuleData.trigger_conditions?.check_criteria?.table || 'cases'} 
                                            onChange={(e) => setAutomationRuleData({ 
                                                ...automationRuleData, 
                                                trigger_conditions: { 
                                                    ...automationRuleData.trigger_conditions, 
                                                    check_criteria: { 
                                                        ...automationRuleData.trigger_conditions?.check_criteria, 
                                                        table: e.target.value 
                                                    }
                                                } 
                                            })}
                                            style={{ width: '100%' }}
                                        >
                                            <option value='cases'>Cases</option>
                                            <option value='leads'>Leads</option>
                                        </select>
                                        <ChevronDown size={16} className="select-chevron" />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className='subtext' htmlFor='check_field'>Check Field</label>
                                    <input 
                                        type="text" 
                                        id='check_field'
                                        placeholder="Field to check (e.g., phase_id, status)" 
                                        value={automationRuleData.trigger_conditions?.check_criteria?.field || ''} 
                                        onChange={(e) => setAutomationRuleData({ 
                                            ...automationRuleData, 
                                            trigger_conditions: { 
                                                ...automationRuleData.trigger_conditions, 
                                                check_criteria: { 
                                                    ...automationRuleData.trigger_conditions?.check_criteria, 
                                                    field: e.target.value 
                                                }
                                            } 
                                        })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label className='subtext' htmlFor='check_value'>Check Value</label>
                                    <input 
                                        type="text" 
                                        id='check_value'
                                        placeholder="Value to check for" 
                                        value={automationRuleData.trigger_conditions?.check_criteria?.value || ''} 
                                        onChange={(e) => setAutomationRuleData({ 
                                            ...automationRuleData, 
                                            trigger_conditions: { 
                                                ...automationRuleData.trigger_conditions, 
                                                check_criteria: { 
                                                    ...automationRuleData.trigger_conditions?.check_criteria, 
                                                    value: e.target.value 
                                                }
                                            } 
                                        })}
                                    />
                                </div>
                            </>
                        )}
                        <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                            <label className='subtext' htmlFor='action_type'>Action Type</label>
                            <div className="select-wrapper" style={{ width: '100%' }}>
                                <select 
                                    className='filter-select' 
                                    id='action_type'
                                    value={automationRuleData.action_type || 'message'} 
                                    onChange={(e) => setAutomationRuleData({ ...automationRuleData, action_type: e.target.value })}
                                    style={{ width: '100%' }}
                                >
                                    <option value='message'>Send Message</option>
                                    <option value='task'>Create Task</option>
                                    <option value='notification'>Send Notification</option>
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                        {automationRuleData.action_type === 'message' && (
                            <>
                                <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                                    <label className='subtext' htmlFor='message_template_id'>Message Template</label>
                                    <div className="select-wrapper" style={{ width: '100%' }}>
                                        <select 
                                            className='filter-select' 
                                            id='message_template_id'
                                            value={automationRuleData.message_template_id || ''} 
                                            onChange={(e) => setAutomationRuleData({ ...automationRuleData, message_template_id: e.target.value })}
                                            style={{ width: '100%' }}
                                        >
                                            <option value=''>Select Template</option>
                                            {messageTemplates.map((template) => (
                                                <option key={template.id} value={template.id}>
                                                    {template.template_name} ({template.message_type})
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} className="select-chevron" />
                                    </div>
                                </div>
                                <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                                    <label className='subtext' htmlFor='notification_target'>Send To</label>
                                    <div className="select-wrapper" style={{ width: '100%' }}>
                                        <select 
                                            className='filter-select' 
                                            id='notification_target'
                                            value={automationRuleData.notification_target || 'contact'} 
                                            onChange={(e) => setAutomationRuleData({ ...automationRuleData, notification_target: e.target.value })}
                                            style={{ width: '100%' }}
                                        >
                                            <option value='contact'>Contact</option>
                                            <option value='user'>Specific User</option>
                                            <option value='team_role'>Team Role</option>
                                        </select>
                                        <ChevronDown size={16} className="select-chevron" />
                                    </div>
                                </div>
                            </>
                        )}
                        {automationRuleData.action_type === 'task' && (
                            <>
                                <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                                    <label className='subtext' htmlFor='message_template_id'>Task Template</label>
                                    <div className="select-wrapper" style={{ width: '100%' }}>
                                        <select 
                                            className='filter-select' 
                                            id='message_template_id'
                                            value={automationRuleData.message_template_id || ''} 
                                            onChange={(e) => setAutomationRuleData({ ...automationRuleData, message_template_id: e.target.value })}
                                            style={{ width: '100%' }}
                                        >
                                            <option value=''>Select Template (for task title/description)</option>
                                            {messageTemplates.map((template) => (
                                                <option key={template.id} value={template.id}>
                                                    {template.template_name}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} className="select-chevron" />
                                    </div>
                                </div>
                                <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                                    <label className='subtext' htmlFor='notification_target'>Assign To</label>
                                    <div className="select-wrapper" style={{ width: '100%' }}>
                                        <select 
                                            className='filter-select' 
                                            id='notification_target'
                                            value={automationRuleData.notification_target || 'user'} 
                                            onChange={(e) => setAutomationRuleData({ ...automationRuleData, notification_target: e.target.value })}
                                            style={{ width: '100%' }}
                                        >
                                            <option value='user'>Specific User</option>
                                            <option value='team_role'>Team Role</option>
                                            <option value='contact'>Contact's Assigned User</option>
                                        </select>
                                        <ChevronDown size={16} className="select-chevron" />
                                    </div>
                                </div>
                                {automationRuleData.notification_target === 'user' && (
                                    <div className="form-group">
                                        <label className='subtext' htmlFor='target_user'>User ID</label>
                                        <input 
                                            type="number" 
                                            id='target_user'
                                            placeholder="User ID to assign task to" 
                                            value={automationRuleData.target_config?.user_id || ''} 
                                            onChange={(e) => setAutomationRuleData({ 
                                                ...automationRuleData, 
                                                target_config: { 
                                                    ...automationRuleData.target_config, 
                                                    user_id: parseInt(e.target.value) 
                                                } 
                                            })}
                                        />
                                    </div>
                                )}
                                {automationRuleData.notification_target === 'team_role' && (
                                    <div className="form-group">
                                        <label className='subtext' htmlFor='team_role'>Team Role</label>
                                        <input 
                                            type="text" 
                                            id='team_role'
                                            placeholder="Team role (1=attorney, 2=paralegal, etc.)" 
                                            value={automationRuleData.target_config?.team_role || ''} 
                                            onChange={(e) => setAutomationRuleData({ 
                                                ...automationRuleData, 
                                                target_config: { 
                                                    ...automationRuleData.target_config, 
                                                    team_role: e.target.value 
                                                } 
                                            })}
                                        />
                                    </div>
                                )}
                                <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                                    <label className='subtext' htmlFor='task_priority'>Priority</label>
                                    <div className="select-wrapper" style={{ width: '100%' }}>
                                        <select 
                                            className='filter-select' 
                                            id='task_priority'
                                            value={automationRuleData.target_config?.priority || 'normal'} 
                                            onChange={(e) => setAutomationRuleData({ 
                                                ...automationRuleData, 
                                                target_config: { 
                                                    ...automationRuleData.target_config, 
                                                    priority: e.target.value 
                                                } 
                                            })}
                                            style={{ width: '100%' }}
                                        >
                                            <option value='low'>Low</option>
                                            <option value='normal'>Normal</option>
                                            <option value='high'>High</option>
                                            <option value='urgent'>Urgent</option>
                                        </select>
                                        <ChevronDown size={16} className="select-chevron" />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label className='subtext' htmlFor='due_in_hours'>Due In (Hours)</label>
                                    <input 
                                        type="number" 
                                        id='due_in_hours'
                                        placeholder="Hours until task is due (optional)" 
                                        value={automationRuleData.target_config?.due_in_hours || ''} 
                                        onChange={(e) => setAutomationRuleData({ 
                                            ...automationRuleData, 
                                            target_config: { 
                                                ...automationRuleData.target_config, 
                                                due_in_hours: parseInt(e.target.value) 
                                            } 
                                        })}
                                    />
                                </div>
                            </>
                        )}
                        <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                            <label className='subtext' htmlFor='message_template_id'>Message Template</label>
                            <div className="select-wrapper" style={{ width: '100%' }}>
                                <select 
                                    className='filter-select' 
                                    id='message_template_id'
                                    value={automationRuleData.message_template_id || ''} 
                                    onChange={(e) => setAutomationRuleData({ ...automationRuleData, message_template_id: e.target.value })}
                                    style={{ width: '100%' }}
                                >
                                    <option value=''>Select Template</option>
                                    {messageTemplates.map((template) => (
                                        <option key={template.id} value={template.id}>
                                            {template.template_name} ({template.message_type})
                                        </option>
                                    ))}
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                        <div className="filter-group" style={{ width: '100%', margin: '10px 0' }}>
                            <label className='subtext' htmlFor='send_to_field'>Send To</label>
                            <div className="select-wrapper" style={{ width: '100%' }}>
                                <select 
                                    className='filter-select' 
                                    id='send_to_field'
                                    value={automationRuleData.send_to_field || 'email'} 
                                    onChange={(e) => setAutomationRuleData({ ...automationRuleData, send_to_field: e.target.value })}
                                    style={{ width: '100%' }}
                                >
                                    <option value='email'>Email</option>
                                    <option value='phone_number'>Phone Number</option>
                                    <option value='cell_phone'>Cell Phone</option>
                                </select>
                                <ChevronDown size={16} className="select-chevron" />
                            </div>
                        </div>
                        <div className="form-group">
                            <label className='subtext' htmlFor='delay_minutes'>Delay (Minutes)</label>
                            <input 
                                type="number" 
                                id='delay_minutes'
                                placeholder="Delay in minutes (0 = immediate)" 
                                value={automationRuleData.delay_minutes || 0} 
                                onChange={(e) => setAutomationRuleData({ ...automationRuleData, delay_minutes: parseInt(e.target.value) })}
                            />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};
